/**
 * Fleet Calendar Page (Flotta Gantt Naptár) — FG-6-1
 * Visual timeline showing all equipment rental periods.
 * Per ERP-TESZT 11 III.5 — Flottakövető Gantt naptár.
 */

import { useTranslation } from '@kgc/i18n';
import { Badge } from '@kgc/ui';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { toISODate } from './components/RentalDateRangeCalendar';

interface FleetCalendarItem {
  equipmentId: string;
  equipmentName: string;
  equipmentCode: string;
  status: string;
  rentals: Array<{
    rentalId: string;
    rentalCode: string;
    partnerName: string;
    startDate: string;
    endDate: string;
    status: string;
  }>;
}

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-red-500',
  OVERDUE: 'bg-purple-600',
  CONFIRMED: 'bg-orange-500',
  EXTENDED: 'bg-red-400',
  RESERVED: 'bg-orange-400',
  PARTIALLY_RETURNED: 'bg-yellow-500',
};

function getDaysInRange(start: Date, end: Date): Date[] {
  const days: Date[] = [];
  const current = new Date(start);
  while (current <= end) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return days;
}

function formatShortDate(date: Date): string {
  return `${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
}

function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

export default function FleetCalendarPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<FleetCalendarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    // toISODate = local dátum-komponensek (#309 bug-class): a toISOString().split
    // Budapest hajnali 0-2h-ban az előző hónapra csúszott (UTC-eltolás).
    return toISODate(d);
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1, 0);
    return toISODate(d);
  });

  useEffect(() => {
    setLoading(true);
    api
      .get<{ data: FleetCalendarItem[] }>(
        `/rental-equipment/fleet-calendar?startDate=${startDate}&endDate=${endDate}`
      )
      .then(res => setData(res.data ?? []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  const days = useMemo(
    () => getDaysInRange(new Date(startDate), new Date(endDate)),
    [startDate, endDate]
  );

  function getRentalForDay(
    rentals: FleetCalendarItem['rentals'],
    day: Date
  ): FleetCalendarItem['rentals'][number] | null {
    const dayStart = day.getTime();
    const dayEnd = dayStart + 86400000;
    for (const r of rentals) {
      const rStart = new Date(r.startDate).getTime();
      const rEnd = new Date(r.endDate).getTime();
      if (rStart < dayEnd && rEnd >= dayStart) return r;
    }
    return null;
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">{t('rental.fleetCalendar.title')}</h1>
        <div className="flex gap-2">
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="rounded border px-2 py-1 text-sm"
          />
          <span className="text-muted-foreground self-center">–</span>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="rounded border px-2 py-1 text-sm"
          />
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-sm">
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded bg-green-500" />{' '}
          {t('rental.fleetCalendar.available')}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded bg-red-500" />{' '}
          {t('rental.fleetCalendar.rented')}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded bg-orange-500" />{' '}
          {t('rental.fleetCalendar.reserved')}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded bg-purple-600" />{' '}
          {t('rental.fleetCalendar.overdue')}
        </span>
      </div>

      {loading ? (
        <div className="py-12 text-center text-muted-foreground">{t('common.loading')}</div>
      ) : data.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          {t('rental.fleetCalendar.noEquipment')}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <div
            className="grid min-w-max"
            style={{
              gridTemplateColumns: `200px repeat(${days.length}, 36px)`,
            }}
          >
            {/* Header row */}
            <div className="sticky left-0 z-10 border-b border-r bg-muted px-2 py-1 text-xs font-semibold text-muted-foreground">
              {t('rental.fleetCalendar.equipment')}
            </div>
            {days.map((day, i) => (
              <div
                key={i}
                className={`border-b px-0.5 py-1 text-center text-[10px] ${
                  isWeekend(day) ? 'bg-muted/50 text-muted-foreground' : 'text-foreground'
                }`}
              >
                {formatShortDate(day)}
              </div>
            ))}

            {/* Equipment rows */}
            {data.map(eq => (
              <>
                <div
                  key={eq.equipmentId}
                  className="sticky left-0 z-10 flex items-center gap-1 border-b border-r bg-background px-2 py-1 text-xs"
                >
                  <span className="truncate font-medium text-foreground" title={eq.equipmentName}>
                    {eq.equipmentName}
                  </span>
                  <Badge variant="outline" className="text-[9px] shrink-0">
                    {eq.equipmentCode}
                  </Badge>
                </div>
                {days.map((day, i) => {
                  const rental = getRentalForDay(eq.rentals, day);
                  const bgColor = rental
                    ? (STATUS_COLORS[rental.status] ?? 'bg-red-500')
                    : isWeekend(day)
                      ? 'bg-muted/30'
                      : 'bg-green-100 dark:bg-green-900/20';
                  return (
                    <div
                      key={`${eq.equipmentId}-${i}`}
                      className={`border-b ${bgColor} cursor-default`}
                      title={
                        rental
                          ? `${rental.partnerName} (${rental.rentalCode})`
                          : t('rental.fleetCalendar.available')
                      }
                    />
                  );
                })}
              </>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
